import { create } from "zustand";

import {
  CUTOUT_CLEARANCE_DEFAULT_MM,
  type CornerMark,
  type Cutout,
  type FacadeFeature,
  type Placement,
} from "@/lib/placement";
import type { SplitPriority } from "@/api";
import { FIT_VIEW, type Viewport } from "@/lib/viewport";

/**
 * Which tool the toolbar is holding. `select` is one of them and stands first
 * (design §5.2).
 *
 * „No tool active" is otherwise an invisible state, and the way out of a mode
 * is then not findable — the classic source of „it keeps drawing things".
 */
export type Tool = "select" | "grating" | "feature" | "bracket" | "area" | "cutout";

/**
 * Which of the three working steps the screen is on — the order the work is
 * done in at K60, as a mechanism rather than a sentence (design 2026-09-22):
 * objects on the facade, then the grating, then the brackets.
 *
 * `null` means „derive it from what stands on the board": no grating yet →
 * objects, grating laid → grating. It becomes a value the moment a person
 * picks a step. Like `tool` and `view`, deliberately **not** in
 * {@link PlanningDraft}: changing the step is not a gesture Rückgängig should
 * undo, and it is written to no server.
 */
export type PlanningStep = "objects" | "grating" | "consoles";

/**
 * Which fixed bracket family the catalogue tries first — the wire values of
 * `PlanInput.consoleSeries` verbatim (E-48). A Vorgang-level setting, not a
 * placement: it lives beside `tool` for the same reason, not inside
 * {@link PlanningDraft} — changing it is not a gesture Rückgängig should
 * undo.
 */
export type ConsoleSeries = "solid_1" | "solid_2";

/**
 * What stands on the board at one moment: the elements, and the corners on
 * mitre.
 *
 * One object and not two stores, because Rückgängig has to undo *the last
 * gesture* — and setting a mitre is one. Two stacks kept in step by hand
 * would be the same bug the mark itself avoids one level down: two things
 * that must agree and can stop agreeing.
 */
export interface PlanningDraft {
  placements: Placement[];
  corners: CornerMark[];
  /** The doors, sills and pipes standing on a wall. Undo carries it exactly
   * as it carries `corners` — one document, one stack. */
  features: FacadeFeature[];
  /** The holes in the grating. One stack with `features`: a hole set is a
   * gesture, and Rückgängig undoes the last gesture. */
  cutouts: Cutout[];
}

/**
 * The empty board, as one shared object.
 *
 * A module constant and not a fresh `{ placements: [], corners: [] }` per
 * call, for the reason the old `history.at(-1) ?? []` needed one: a new
 * object identity on every render re-runs every memo that depends on it.
 */
export const EMPTY_DRAFT: PlanningDraft = {
  placements: [],
  corners: [],
  features: [],
  cutouts: [],
};

export interface CutoutSurcharge {
  typed: string | null;
  displaced: string | null;
}

/** Everything a Vorgang carries that is neither geometry nor a placement —
 * the backend `PlanningStateIn` minus `placements`. The name stayed when the
 * cutout fields joined (12.09.2026): every caller already spelled it. */
export interface ConsolePreferences {
  consoleSeries: ConsoleSeries;
  consoleAdjustableFirst: boolean;
  consoleOverrides: Record<string, string>;
  /** Montagespiel for a linked downpipe hole (D-10, AN-26). Only the prefill reads it. */
  cutoutClearanceMm: number;
  /** The surcharge formula's two parameters, as strings — empty until Chiara names them (D-6). */
  cutoutRatePerMNet: string | null;
  cutoutBaseNet: string | null;
  /** Hole id → what somebody typed, and what that displaced. */
  cutoutSurcharges: Record<string, CutoutSurcharge>;
  /** Which splitting rule „Wand belegen" lays outside a width zone (PRD
   * §22.5 Fall C) — a prefill, never a re-plan of what already lies. */
  splitPriority: SplitPriority;
}

/**
 * The placements somebody is laying, and every step it took to get there —
 * for as long as this browser tab stays open.
 *
 * **What this is not.** It is not a case, not a job, not a saved document.
 * Every gesture this store's `push` records is also written to the server as
 * a version (task 15, design §5, §9) — that write, and only that write,
 * survives a reload. This store itself is the *editor's* state: the undo
 * stack, the current selection, the tool in hand — none of which the server
 * needs to know or keep. Closing the tab mid-gesture loses nothing that
 * matters: the last accepted version is on the server, and reopening the
 * Vorgang replays it.
 */
interface PlanningState {
  /** Every committed step, oldest first. `[]` before anything was placed. */
  history: PlanningDraft[];
  /**
   * The steps Rückgängig took off {@link history}, the most recent last —
   * what Wiederholen puts back, one at a time.
   *
   * The same drafts, not a second model: Wiederholen is Rückgängig walked the
   * other way, and the screen writes it as a Stand the same way (`diffOps`).
   * Any new step empties it — a gesture after Rückgängig starts another
   * branch, and putting the old one back on top would replay a board nobody
   * is looking at. So does everything that replaces the board wholesale
   * ({@link hydrate}, {@link discard}, {@link clear}).
   */
  future: PlanningDraft[];
  tool: Tool;
  /** The step somebody picked, or `null` for „follow the board". */
  step: PlanningStep | null;
  /** The element the side panel is working on, or `null`. */
  selected: string | null;
  /** The wall the side panel is working on, or `null`. */
  selectedWall: number | null;
  /** The facade object the side panel is working on, or `null`. Its own slot
   * beside `selected`: an object is not a placement, the two panels ask
   * different questions, and „nothing is selected" has to stay one state. */
  selectedFeature: string | null;
  /** The hole the side panel is working on, or `null` — a fourth slot, for
   * the reason `selectedFeature` is a third. */
  selectedCutout: string | null;

  /**
   * The three settings task 11 puts in the person's hand — a Vorgang-level
   * preference (`consoleSeries`, `consoleAdjustableFirst`) and one correction
   * per bracket (`consoleOverrides`), all forwarded on every planning request
   * verbatim, and on every version this screen writes (task 15).
   */
  consoleSeries: ConsoleSeries;
  consoleAdjustableFirst: boolean;
  /** Bracket address (`DemoConsoleMark.key`) → article number, for the ones a
   * person set by hand. */
  consoleOverrides: Record<string, string>;

  /** The cutout half of {@link ConsolePreferences} — Vorgang-level like the
   * bracket settings, and for the same reason outside {@link PlanningDraft}:
   * typing a rate is not a gesture Rückgängig should undo. */
  cutoutClearanceMm: number;
  cutoutRatePerMNet: string | null;
  cutoutBaseNet: string | null;
  cutoutSurcharges: Record<string, CutoutSurcharge>;

  /** The splitting rule of {@link ConsolePreferences} — Vorgang-level like
   * the bracket settings, forwarded on every planning request and every
   * version. Set by the „Aufteilung" select in step 2 (Fall C). */
  splitPriority: SplitPriority;

  /**
   * What part of the drawing is on screen.
   *
   * Beside `tool` and `consoleSeries`, deliberately **not** in
   * {@link PlanningDraft}: turning the wheel is not a gesture Rückgängig
   * should undo, and every entry in `history` is written back to the server
   * as a Stand. A zoom that wrote a version would post one per wheel notch.
   */
  view: Viewport;

  /** Every step at the bottom of {@link history} that is **not** a gesture of
   * this session — 1 after a Vorgang was hydrated from its stored version, 0
   * otherwise.
   *
   * Rückgängig may not walk below it. Reopening a case puts the stored plan on
   * the stack so the board has something to draw; undoing *that* would empty a
   * board nobody in this session filled, and — since every gesture is written
   * back — post the empty board as the next Stand. */
  baseSteps: number;

  /**
   * The wall and the step the **next** {@link hydrate} keeps instead of
   * letting go of them — set by a measure gesture on the board (spec
   * 2026-10-15 §2) right before it asks for the plan again.
   *
   * A hydration normally means „the server's news, start over": a reload
   * after a 409, another Vorgang. After „Maß“ it is the gesture's own answer,
   * and dropping the wall would close the very panel the ring's sentence is
   * about to appear in. One hydration consumes it; `clear()` forgets it.
   * The index is safe to keep: a measure gesture changes no wall's position
   * in the list, only its length.
   */
  heldPick: { wallIndex: number; step: PlanningStep | null } | null;

  /**
   * The board asked for the server's state after a refused write (a 409, a
   * refusal outright) — so the **next** plan answer goes on the board even
   * where it is the very answer already cached.
   *
   * Without it nothing came: the refetch after a refused gesture brings back
   * exactly what the server held before it, the query cache keeps the old
   * object for a deep-equal answer, and `CaseRoute` hydrates on a *new*
   * answer only. The board kept the refused gesture (15 elements where the
   * server held 16) and, still waiting for that hydration, wrote nothing ever
   * again (screen check 15.10.2026, V-2). Set by the screen before it asks,
   * read by `CaseRoute`, consumed by {@link hydrate}; `clear()` forgets it.
   *
   * A time (`Date.now()`, the clock `dataUpdatedAt` is read on), not a flag:
   * only a read that landed **after** the request answers it. One that
   * landed before could predate a write the board made since, and putting
   * that back would undo a gesture the server holds.
   */
  hydrationAskedAt: number | null;

  /**
   * The board takes no writing gesture for now — a measure or a change of
   * type is being saved and its plan read again, and the hydration that
   * answers it puts the server's board on, which knows nothing of a gesture
   * made meanwhile (re-review of 41503767, N-1). Every gesture that changes
   * the board goes through one of the writing mutations below, and each is a
   * no-op while this stands: one gate, not one per handler. Selection, tool,
   * step, view and hydration stay open. Set by the screen, bounded there.
   */
  held: boolean;
  /** See {@link held}. */
  setHeld(held: boolean): void;
  /**
   * A step was dropped while {@link held} — the first frame of a gesture
   * begun in the hold (review of 89ca82a9, B-3). Its next `live` frame then
   * opens a step of its own instead of overwriting the one below, which is
   * the Stand the board stands on; the next accepted step clears it.
   */
  droppedWhileHeld: boolean;

  /** A counter, bumped by {@link hydrate}. Nothing reads its value; the screen
   * watches it to know that what stands on the board came from the server and
   * not from a person, and that the settle it is about to see is therefore not
   * a gesture to write back. */
  hydratedAt: number;

  /** What stands now. {@link EMPTY_DRAFT} before anything was placed. */
  current(): PlanningDraft;
  canUndo(): boolean;
  /** Whether Wiederholen has something to put back — see {@link future}. */
  canRedo(): boolean;
  /**
   * Put a stored Vorgang on the board: the draft it was left in, and the three
   * bracket settings that were in force (design §5 — reopening a case is a
   * replay).
   *
   * Clears first, unconditionally: the store outlives a route change, and a
   * second Vorgang opened over the first would otherwise inherit its elements.
   */
  hydrate(draft: PlanningDraft, prefs: ConsolePreferences): void;
  /** Keep this wall and step through the next {@link hydrate} — see {@link heldPick}. */
  holdPick(pick: { wallIndex: number; step: PlanningStep | null } | null): void;
  /** The next plan answer replaces the board, changed or not — see {@link hydrationAskedAt}. */
  askHydration(): void;
  /**
   * Record a step. `live` folds a gesture's many frames into one entry, so
   * Rückgängig undoes the gesture rather than a pixel of it.
   */
  push(next: PlanningDraft, live?: boolean): void;
  undo(): void;
  /** Put back the step the last Rückgängig took — see {@link future}. */
  redo(): void;
  setView(view: Viewport): void;
  setTool(tool: Tool): void;
  setStep(step: PlanningStep | null): void;
  select(id: string | null): void;
  selectWall(index: number | null): void;
  selectFeature(id: string | null): void;
  selectCutout(id: string | null): void;
  setConsoleSeries(series: ConsoleSeries): void;
  setConsoleAdjustableFirst(value: boolean): void;
  setSplitPriority(priority: SplitPriority): void;
  /**
   * Set one bracket's article by hand, or clear it with `articleNo: null` —
   * „Vorschlag wiederherstellen". Keyed the same way `DemoConsoleMark.key` is,
   * never by an index: a neighbouring placement moving must not make this
   * land on a bracket nobody meant.
   */
  setConsoleOverride(key: string, articleNo: string | null): void;
  setCutoutClearanceMm(mm: number): void;
  setCutoutRates(ratePerMNet: string | null, baseNet: string | null): void;
  /** `typed: null` clears the entry — „Rechnung wiederherstellen". */
  setCutoutSurcharge(id: string, typed: string | null, displaced: string | null): void;
  /** Throw the draft away. What „Verwerfen" does — in memory only since task
   * 15: there is no browser storage left to clear. */
  discard(): void;
  /** Forget everything in memory. Same effect as `discard()` plus the tool
   * and the two console preferences — used between tests and on a fresh
   * mount, never by a person's own click. */
  clear(): void;
}

export const usePlanning = create<PlanningState>((set, get) => ({
  history: [],
  future: [],
  tool: "select",
  step: null,
  selected: null,
  selectedWall: null,
  selectedFeature: null,
  selectedCutout: null,
  consoleSeries: "solid_1",
  consoleAdjustableFirst: false,
  consoleOverrides: {},
  cutoutClearanceMm: CUTOUT_CLEARANCE_DEFAULT_MM,
  cutoutRatePerMNet: null,
  cutoutBaseNet: null,
  cutoutSurcharges: {},
  splitPriority: "standard_first",
  view: FIT_VIEW,
  baseSteps: 0,
  heldPick: null,
  hydrationAskedAt: null,
  hydratedAt: 0,
  held: false,
  droppedWhileHeld: false,

  current() {
    return get().history.at(-1) ?? EMPTY_DRAFT;
  },

  canUndo() {
    return get().history.length > get().baseSteps;
  },

  canRedo() {
    return get().future.length > 0;
  },

  hydrate(draft, prefs) {
    set((state) => ({
      history: [draft],
      future: [],
      baseSteps: 1,
      droppedWhileHeld: false,
      hydratedAt: state.hydratedAt + 1,
      tool: "select",
      // A reopened Vorgang lands on the step its board is at, not on the one
      // somebody left another Vorgang on — unless this hydration answers a
      // measure gesture made right here (`heldPick`).
      step: state.heldPick ? state.heldPick.step : null,
      selected: null,
      selectedWall: state.heldPick?.wallIndex ?? null,
      heldPick: null,
      hydrationAskedAt: null,
      selectedFeature: null,
      selectedCutout: null,
      view: FIT_VIEW,
      consoleSeries: prefs.consoleSeries,
      consoleAdjustableFirst: prefs.consoleAdjustableFirst,
      consoleOverrides: prefs.consoleOverrides,
      cutoutClearanceMm: prefs.cutoutClearanceMm,
      cutoutRatePerMNet: prefs.cutoutRatePerMNet,
      cutoutBaseNet: prefs.cutoutBaseNet,
      cutoutSurcharges: prefs.cutoutSurcharges,
      splitPriority: prefs.splitPriority,
    }));
  },

  holdPick(pick) {
    set({ heldPick: pick });
  },

  askHydration() {
    set({ hydrationAskedAt: Date.now() });
  },

  setHeld(held) {
    set({ held });
  },

  setView(view) {
    set({ view });
  },

  setConsoleSeries(series) {
    if (get().held) return;
    set({ consoleSeries: series });
  },

  setConsoleAdjustableFirst(value) {
    if (get().held) return;
    set({ consoleAdjustableFirst: value });
  },

  setSplitPriority(priority) {
    if (get().held) return;
    set({ splitPriority: priority });
  },

  setConsoleOverride(key, articleNo) {
    if (get().held) return;
    set((state) => {
      const next = { ...state.consoleOverrides };
      if (articleNo === null) delete next[key];
      else next[key] = articleNo;
      return { consoleOverrides: next };
    });
  },

  setCutoutClearanceMm(mm) {
    if (get().held) return;
    set({ cutoutClearanceMm: mm });
  },

  setCutoutRates(ratePerMNet, baseNet) {
    if (get().held) return;
    set({ cutoutRatePerMNet: ratePerMNet, cutoutBaseNet: baseNet });
  },

  setCutoutSurcharge(id, typed, displaced) {
    if (get().held) return;
    set((state) => {
      const next = { ...state.cutoutSurcharges };
      if (typed === null) delete next[id];
      else next[id] = { typed, displaced };
      return { cutoutSurcharges: next };
    });
  },

  push(next, live = false) {
    if (get().held) {
      set({ droppedWhileHeld: true });
      return;
    }
    set((state) => ({
      history:
        live && state.history.length > 0 && !state.droppedWhileHeld
          ? [...state.history.slice(0, -1), next]
          : [...state.history, next],
      future: [],
      droppedWhileHeld: false,
    }));
  },

  undo() {
    if (get().held) return;
    // Never below the stored Stand this Vorgang was opened on: that step is
    // not a gesture of this session, and undoing it would post an empty board
    // as the next version.
    if (get().history.length <= get().baseSteps) return;
    set((state) => ({
      history: state.history.slice(0, -1),
      future: [...state.future, state.history.at(-1)!],
      selected: null,
      // What Rückgängig undid may have been the object the panel is on —
      // and an object that is no longer in the draft must not keep a panel
      // open over it. The same for a hole.
      selectedFeature: null,
      selectedCutout: null,
    }));
  },

  redo() {
    if (get().held) return;
    const next = get().future.at(-1);
    if (next === undefined) return;
    set((state) => ({
      history: [...state.history, next],
      future: state.future.slice(0, -1),
      // What comes back may take away the thing a panel is open over — a
      // removal put back — for the reason Rückgängig lets go of it.
      selected: null,
      selectedFeature: null,
      selectedCutout: null,
    }));
  },

  setTool(tool) {
    // The element selection goes, the wall stays: picking up a tool is about
    // what happens next, and „Wand belegen" acts on the wall that is chosen.
    set({ tool, selected: null, selectedFeature: null, selectedCutout: null });
  },

  setStep(step) {
    // The element selection stays: switching from „Roste" to „Konsolen" to
    // read the derivation must not throw away the element somebody was
    // looking at. The tool stays too — the screen decides whether it belongs
    // to the step (`effectiveTool`), and hands it back on the way back in.
    //
    // **A picked wall does not.** Its panel („Wand belegen", the Fall-C
    // variants) stood in for the step's own panel, so a wall picked while
    // setting a Haustür in step 1 hid „Alle Wände belegen" in step 2 and the
    // console settings in step 3 (Generalprobe 15.10.2026).
    set({ step, selectedWall: null });
  },

  select(id) {
    set({
      selected: id,
      selectedWall: id === null ? get().selectedWall : null,
      selectedFeature: id === null ? get().selectedFeature : null,
      selectedCutout: id === null ? get().selectedCutout : null,
    });
  },

  selectWall(index) {
    set({
      selectedWall: index,
      selected: index === null ? get().selected : null,
      selectedFeature: index === null ? get().selectedFeature : null,
      selectedCutout: index === null ? get().selectedCutout : null,
    });
  },

  selectFeature(id) {
    set({
      selectedFeature: id,
      selected: id === null ? get().selected : null,
      selectedWall: id === null ? get().selectedWall : null,
      selectedCutout: id === null ? get().selectedCutout : null,
    });
  },

  selectCutout(id) {
    set({
      selectedCutout: id,
      selected: id === null ? get().selected : null,
      selectedWall: id === null ? get().selectedWall : null,
      selectedFeature: id === null ? get().selectedFeature : null,
    });
  },

  discard() {
    set({
      history: [],
      future: [],
      baseSteps: 0,
      selected: null,
      selectedWall: null,
      selectedFeature: null,
      selectedCutout: null,
      consoleOverrides: {},
      cutoutSurcharges: {},
    });
  },

  clear() {
    set({
      held: false,
      droppedWhileHeld: false,
      history: [],
      future: [],
      baseSteps: 0,
      heldPick: null,
      hydrationAskedAt: null,
      tool: "select",
      step: null,
      selected: null,
      selectedWall: null,
      selectedFeature: null,
      selectedCutout: null,
      view: FIT_VIEW,
      consoleSeries: "solid_1",
      consoleAdjustableFirst: false,
      consoleOverrides: {},
      cutoutClearanceMm: CUTOUT_CLEARANCE_DEFAULT_MM,
      cutoutRatePerMNet: null,
      cutoutBaseNet: null,
      cutoutSurcharges: {},
      splitPriority: "standard_first",
    });
  },
}));
